/**
 * Canonical types for target verticals + a built-in fallback list.
 *
 * The actual source of truth is the `vertical_seeds` DB table, surfaced via
 * the `useVerticals()` hook. The constants below are kept as a fallback so
 * SSR-style/code paths that can't await a query (and the very first render
 * before the query resolves) still have something sensible to show.
 */
export interface VerticalIcpDefaults {
  titles: string[];
  industries: string[];
  company_sizes: string[];
  regions: string[];
  pain_points: string[];
}

export interface VerticalOption {
  slug: string;
  label: string;
  description?: string;
  icpDefaults?: VerticalIcpDefaults;
}

export const VERTICAL_OPTIONS: VerticalOption[] = [
  {
    slug: "series-a-b2b-saas-na",
    label: "Series-A B2B SaaS founders",
    description: "20–200 employees, North America",
    icpDefaults: {
      titles: ["Founder", "CEO", "Co-Founder", "Head of Sales", "VP Sales", "Head of Growth"],
      industries: ["B2B SaaS", "Software"],
      company_sizes: ["20-50", "51-200"],
      regions: ["United States", "Canada"],
      pain_points: [
        "Founder still doing outbound personally",
        "Inconsistent pipeline after seed round",
        "First sales hires not yet ramped",
      ],
    },
  },
  {
    slug: "seed-b2b-saas-na",
    label: "Seed-stage B2B SaaS founders",
    description: "Pre-Series-A, North America",
    icpDefaults: {
      titles: ["Founder", "CEO", "Co-Founder", "Founding AE"],
      industries: ["B2B SaaS", "Software"],
      company_sizes: ["1-10", "11-20"],
      regions: ["United States", "Canada"],
      pain_points: [
        "No dedicated SDR yet",
        "Founder-led sales doesn't scale",
        "Need first 10 design partners",
      ],
    },
  },
  {
    slug: "series-bc-b2b-saas-na",
    label: "Series B/C B2B SaaS",
    description: "200–1000 employees, North America",
    icpDefaults: {
      titles: ["VP Sales", "Chief Revenue Officer", "Head of Sales Development", "Director of Demand Generation"],
      industries: ["B2B SaaS", "Software"],
      company_sizes: ["201-500", "501-1000"],
      regions: ["United States", "Canada"],
      pain_points: [
        "SDR cost-per-meeting climbing",
        "Outbound team underperforming quota",
        "Need predictable pipeline coverage",
      ],
    },
  },
  {
    slug: "agencies-na",
    label: "Marketing & creative agencies",
    description: "10–100 employees, North America",
    icpDefaults: {
      titles: ["Founder", "Managing Director", "Head of New Business", "Business Development Director"],
      industries: ["Marketing & Advertising", "Creative Agency", "Design Agency"],
      company_sizes: ["11-50", "51-100"],
      regions: ["United States", "Canada"],
      pain_points: [
        "Referral pipeline drying up",
        "No dedicated new-business lead",
        "Inconsistent inbound from content",
      ],
    },
  },
  {
    slug: "professional-services-na",
    label: "Professional services firms",
    description: "Consulting, legal, accounting, North America",
    icpDefaults: {
      titles: ["Managing Partner", "Partner", "Practice Lead", "Head of Business Development"],
      industries: ["Management Consulting", "Legal Services", "Accounting"],
      company_sizes: ["11-50", "51-200"],
      regions: ["United States", "Canada"],
      pain_points: [
        "Partners are the only rainmakers",
        "Long sales cycles need warmer entry",
        "No systematic outbound motion",
      ],
    },
  },
];

export const DEFAULT_VERTICAL_SLUG = "series-a-b2b-saas-na";

export function verticalLabel(
  slug?: string | null,
  list: VerticalOption[] = VERTICAL_OPTIONS,
): string | null {
  if (!slug) return null;
  return list.find((v) => v.slug === slug)?.label ?? null;
}

export function verticalIcpDefaults(
  slug?: string | null,
  list: VerticalOption[] = VERTICAL_OPTIONS,
): VerticalIcpDefaults | null {
  if (!slug) return null;
  return list.find((v) => v.slug === slug)?.icpDefaults ?? null;
}
